// Shared helpers for deal-checker history. Extracted from CheckDealTags so the
// Manage page can render the same batch history inline without duplicating logic.
import * as XLSX from 'xlsx';

// Flatten deal-checker tasks into per-ASIN result rows, prioritising task status
// over payload contents, then sort newest-first.
export const flattenDealCheckerTasks = (tasks) => {
    const flattenedResults = [];
    (tasks || []).forEach(task => {
        const taskStatus = task.task_status || 'PENDING';

        // Check task status FIRST - prioritize status over results
        if (taskStatus === 'PENDING') {
            // Task created but Celery hasn't picked it up yet
            task.asins.forEach(asin => {
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: asin,
                    title: 'Pending...',
                    deal_present: false,
                    deal_badge: 'Pending',
                    has_coupon: false,
                    coupon_text: 'N/A',
                    deal_details: null,
                    current_price: null,
                    original_price: null,
                    savings_amount: null,
                    discount_percentage: null,
                    sold_by: null,
                    delivery_info: null,
                    zipcode: null,
                    status: 'PENDING'
                });
            });
        } else if (taskStatus === 'PROGRESS') {
            // Task picked up by Celery and is being processed
            task.asins.forEach(asin => {
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: asin,
                    title: 'Processing...',
                    deal_present: false,
                    deal_badge: 'Processing',
                    has_coupon: false,
                    coupon_text: 'N/A',
                    deal_details: null,
                    current_price: null,
                    original_price: null,
                    savings_amount: null,
                    discount_percentage: null,
                    sold_by: null,
                    delivery_info: null,
                    zipcode: null,
                    status: 'PROGRESS'
                });
            });
        } else if (taskStatus === 'SUCCESS') {
            // Task completed successfully - show results
            if (task.result && task.result.success && task.result.results && task.result.results.length > 0) {
                task.result.results.forEach(result => {
                    flattenedResults.push({
                        date: task.created_at,
                        task_id: task.task_id,
                        asin: result.asin,
                        title: result.title || 'Not applicable',
                        deal_present: result.has_deal || false,
                        deal_badge: result.deal_badge || result.deal_type || 'Not applicable',
                        deal_details: result,
                        current_price: result.current_price || null,
                        original_price: result.original_price || null,
                        savings_amount: result.savings_amount || null,
                        discount_percentage: result.discount_percentage || null,
                        sold_by: result.sold_by || 'Not applicable',
                        delivery_info: result.delivery_info || 'Not applicable',
                        zipcode: result.zipcode ? String(result.zipcode) : null,
                        main_image: result.main_image || '',
                        has_coupon: result.has_coupon ?? false,
                        coupon_text: result.coupon_text || 'N/A',
                        status: 'SUCCESS'
                    });
                });
            } else {
                // SUCCESS status but no results - show ASINs with N/A
                task.asins.forEach(asin => {
                    flattenedResults.push({
                        date: task.created_at,
                        task_id: task.task_id,
                        asin: asin,
                        title: 'Not applicable',
                        deal_present: false,
                        deal_badge: 'Not applicable',
                        has_coupon: false,
                        coupon_text: 'N/A',
                        deal_details: null,
                        current_price: null,
                        original_price: null,
                        savings_amount: null,
                        discount_percentage: null,
                        sold_by: null,
                        delivery_info: null,
                        zipcode: null,
                        status: 'SUCCESS'
                    });
                });
            }
        } else if (taskStatus === 'PARTIAL_SUCCESS') {
            // Some ASINs succeeded, some failed. Successful ones live in
            // task.result.results; failed ones in task.result.failed_asins.
            const successResults = (task.result && task.result.results) || [];
            const failedAsins = (task.result && task.result.failed_asins) || [];
            const renderedAsins = new Set();

            successResults.forEach(result => {
                renderedAsins.add(result.asin);
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: result.asin,
                    title: result.title || 'Not applicable',
                    deal_present: result.has_deal || false,
                    deal_badge: result.deal_badge || result.deal_type || 'Not applicable',
                    deal_details: result,
                    current_price: result.current_price || null,
                    original_price: result.original_price || null,
                    savings_amount: result.savings_amount || null,
                    discount_percentage: result.discount_percentage || null,
                    sold_by: result.sold_by || 'Not applicable',
                    delivery_info: result.delivery_info || 'Not applicable',
                    zipcode: result.zipcode ? String(result.zipcode) : null,
                    main_image: result.main_image || '',
                    has_coupon: result.has_coupon ?? false,
                    coupon_text: result.coupon_text || 'N/A',
                    status: 'SUCCESS'
                });
            });

            failedAsins.forEach(failed => {
                const asin = failed && failed.asin;
                if (!asin || renderedAsins.has(asin)) return;
                renderedAsins.add(asin);
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: asin,
                    title: 'Failed',
                    deal_present: false,
                    deal_badge: 'Failed',
                    has_coupon: false,
                    coupon_text: 'N/A',
                    deal_details: null,
                    current_price: null,
                    original_price: null,
                    savings_amount: null,
                    discount_percentage: null,
                    sold_by: null,
                    delivery_info: null,
                    zipcode: null,
                    status: 'FAILURE'
                });
            });

            // Safety net: any submitted ASIN not represented in results or
            // failed_asins (shouldn't happen, but guards against shape drift).
            (task.asins || []).forEach(asin => {
                if (renderedAsins.has(asin)) return;
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: asin,
                    title: 'Failed',
                    deal_present: false,
                    deal_badge: 'Failed',
                    has_coupon: false,
                    coupon_text: 'N/A',
                    deal_details: null,
                    current_price: null,
                    original_price: null,
                    savings_amount: null,
                    discount_percentage: null,
                    sold_by: null,
                    delivery_info: null,
                    zipcode: null,
                    status: 'FAILURE'
                });
            });
        } else if (taskStatus === 'FAILURE') {
            // Task failed
            task.asins.forEach(asin => {
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: asin,
                    title: 'Failed',
                    deal_present: false,
                    deal_badge: 'Failed',
                    has_coupon: false,
                    coupon_text: 'N/A',
                    deal_details: null,
                    current_price: null,
                    original_price: null,
                    savings_amount: null,
                    discount_percentage: null,
                    sold_by: null,
                    delivery_info: null,
                    zipcode: null,
                    status: 'FAILURE'
                });
            });
        } else {
            // Unknown status - show ASINs with status
            task.asins.forEach(asin => {
                flattenedResults.push({
                    date: task.created_at,
                    task_id: task.task_id,
                    asin: asin,
                    title: 'Unknown status',
                    deal_present: false,
                    deal_badge: taskStatus,
                    has_coupon: false,
                    coupon_text: 'N/A',
                    deal_details: null,
                    current_price: null,
                    original_price: null,
                    savings_amount: null,
                    discount_percentage: null,
                    sold_by: null,
                    delivery_info: null,
                    zipcode: null,
                    status: taskStatus
                });
            });
        }
    });

    // Sort by date (newest first)
    flattenedResults.sort((a, b) => new Date(b.date) - new Date(a.date));
    return flattenedResults;
};

// Group flattened results by task_id (batch), sorted by date.
export const groupResultsByTask = (results, sortOrder = 'newest') => {
    const byTask = {};
    for (const item of (results || [])) {
        const tid = item.task_id || 'unknown';
        if (!byTask[tid]) {
            byTask[tid] = { task_id: tid, date: item.date, results: [] };
        }
        byTask[tid].results.push(item);
        if (item.date && (!byTask[tid].date || new Date(item.date) > new Date(byTask[tid].date))) {
            byTask[tid].date = item.date;
        }
    }
    return Object.values(byTask).sort((a, b) =>
        sortOrder === 'oldest'
            ? new Date(a.date) - new Date(b.date)
            : new Date(b.date) - new Date(a.date)
    );
};

// How many thumbnails to show and grid layout: 2→2, 3→3, 4→4, 5→4, 6→6, 7→6, 8+→8.
// Grid cols: 1→1, 2→2, 3→3, 4→2, 6→3, 8→4.
export const getThumbnailDisplay = (count) => {
    const displayCount = count <= 4 ? count : count === 5 ? 4 : count <= 7 ? 6 : 8;
    const gridCols = displayCount <= 1 ? 1 : displayCount <= 3 ? displayCount : displayCount === 4 ? 2 : displayCount === 6 ? 3 : 4;
    return { displayCount, gridCols };
};

export const formatBatchDateLong = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const dateStr = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    const timeStr = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    return `${dateStr} | ${timeStr}`;
};

// Thumbnail URL for ASIN (placeholder; can be replaced with real image API)
export const getAsinThumbnailUrl = (asin) => {
    if (!asin) return 'https://placehold.co/43x42?text=ASIN';
    return `https://placehold.co/43x42?text=${encodeURIComponent(asin.slice(-4))}`;
};

// Extract percentage from coupon_text (e.g. "Apply 2% coupon" -> "2%")
export const formatCouponText = (text) => {
    if (!text || text === 'N/A' || text === 'Not applicable') return '-';
    const m = String(text).match(/\d+%?/);
    return m ? (m[0].endsWith('%') ? m[0] : m[0] + '%') : text;
};

// Map a single result item to an Excel row (shared by history and validation batch export)
export const mapItemToExcelRow = (item) => {
    const dealType = item.deal_details?.deal_type ||
        (item.deal_badge && item.deal_badge !== 'Not applicable' && item.deal_badge !== 'Pending' && item.deal_badge !== 'Processing' && item.deal_badge !== 'Failed' ? item.deal_badge : '-');
    const deliveryInfo = item.delivery_info && item.delivery_info !== 'N/A' && item.delivery_info !== 'Not applicable' ? item.delivery_info : null;
    const deliveryParts = deliveryInfo ? deliveryInfo.split(/\s+or\s+/i) : [];
    const nonPrimeDelivery = deliveryParts[0]?.trim() || (deliveryInfo || null);
    const primeDelivery = deliveryParts[1]?.trim() || null;
    const parsePriceToNumber = (value) => {
        if (!value || typeof value !== 'string') return null;
        const cleaned = value.replace(/[^\d.,]/g, '').replace(/,/g, '');
        const num = Number(cleaned);
        return Number.isFinite(num) ? num : null;
    };
    const normalizeDiscount = (value) => {
        if (value == null) return null;
        const s = String(value).trim();
        if (!s || s === '-' || s.toLowerCase() === 'n/a' || s.toLowerCase() === 'not applicable') return null;
        if (s.includes('%')) return s;
        const num = Number(s.replace(/[^\d.]/g, ''));
        return Number.isFinite(num) ? `${Math.round(num)}%` : s;
    };
    const discountFromBackend =
        normalizeDiscount(item.discount_percentage) ||
        normalizeDiscount(item.deal_details?.discount_percentage) ||
        normalizeDiscount(item.deal_details?.discount_percent) ||
        normalizeDiscount(item.deal_details?.discount);
    const mrpNum = parsePriceToNumber(item.original_price);
    const dealNum = parsePriceToNumber(item.current_price);
    const computedDiscount = mrpNum && dealNum && mrpNum > 0 && mrpNum >= dealNum ? `${Math.round(((mrpNum - dealNum) / mrpNum) * 100)}%` : null;
    return {
        'Date': new Date(item.date).toLocaleDateString(),
        'ASIN': item.asin || '-',
        'Title': item.title && item.title !== 'Pending...' && item.title !== 'Processing...' ? item.title : '-',
        'Deal Present': item.deal_present ? 'Yes' : 'No',
        'Deal Badge': item.deal_badge && item.deal_badge !== 'Not applicable' && item.deal_badge !== 'Pending' && item.deal_badge !== 'Processing' && item.deal_badge !== 'Failed' ? item.deal_badge : '-',
        'Deal Type': dealType,
        'Current Price/Deal Price': item.current_price && item.current_price !== 'N/A' ? item.current_price : '-',
        'MRP': item.original_price && item.original_price !== 'N/A' ? item.original_price : '-',
        'Savings Amount': item.savings_amount && item.savings_amount !== 'N/A' ? item.savings_amount : '-',
        'Discount Percentage': discountFromBackend || computedDiscount || '-',
        'Buybox Winner': item.sold_by && item.sold_by !== 'N/A' && item.sold_by !== 'Not applicable' ? item.sold_by : '-',
        'Coupon': (item.has_coupon && (item.coupon_text || item.deal_details?.coupon_text) && String(item.coupon_text || item.deal_details?.coupon_text || '').trim() !== 'N/A') ? formatCouponText(item.coupon_text || item.deal_details?.coupon_text) : 'Not Applicable',
        'Prime Delivery': primeDelivery || '-',
        'Non Prime Delivery': nonPrimeDelivery || '-',
        'Pincode': item.zipcode || '-',
        'Status': item.status || '-'
    };
};

export const buildDealCheckWorkbook = (excelData) => {
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Deal Check Results');
    const columnWidths = [
        { wch: 12 }, { wch: 15 }, { wch: 50 }, { wch: 12 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 15 }, { wch: 18 }, { wch: 20 }, { wch: 14 }, { wch: 45 }, { wch: 60 }, { wch: 10 }, { wch: 12 }
    ];
    worksheet['!cols'] = columnWidths;
    return XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
};

// Group a batch's results by ASIN for the Deal Tag Validation detail view.
// Applies the search / product-status / deal-type filters.
export const groupBatchByAsin = (batch, { search = '', productStatusFilter = 'all', dealType = '' } = {}) => {
    if (!batch || !batch.results || !batch.results.length) return [];
    const q = (search || '').toLowerCase().trim();
    let results = batch.results;
    if (q) {
        results = results.filter(r =>
            (r.asin && r.asin.toLowerCase().includes(q)) ||
            (r.title && r.title.toLowerCase().includes(q))
        );
    }
    if (productStatusFilter === 'with_deals') results = results.filter(r => r.deal_present);
    else if (productStatusFilter === 'no_deals') results = results.filter(r => !r.deal_present);
    if (dealType) {
        results = results.filter(r => r.deal_badge && String(r.deal_badge).trim() === dealType);
    }
    const byAsin = {};
    results.forEach(r => {
        const key = r.asin || 'unknown';
        if (!byAsin[key]) {
            byAsin[key] = {
                asin: key,
                title: r.title || 'N/A',
                // Prefer the real product image (if available), otherwise fall back to the ASIN-based placeholder
                imageUrl: r.main_image || getAsinThumbnailUrl(r.asin),
                rows: [],
            };
        }
        const deliveryInfo = r.delivery_info && r.delivery_info !== 'N/A' && r.delivery_info !== 'Not applicable' ? r.delivery_info : null;
        const deliveryParts = deliveryInfo ? deliveryInfo.split(/\s+or\s+/i) : [];
        byAsin[key].rows.push({
            zipcode: r.zipcode ? String(r.zipcode) : '-',
            deal_badge: r.deal_badge,
            deal_present: r.deal_present,
            sold_by: r.sold_by,
            delivery_info: r.delivery_info,
            standardDelivery: deliveryParts[0]?.trim() || deliveryInfo,
            primeDelivery: deliveryParts[1]?.trim() || null,
            has_coupon: r.has_coupon ?? false,
            coupon_text: r.coupon_text || 'N/A',
            current_price: r.current_price || 'N/A',
        });
    });
    return Object.values(byAsin);
};

// Unique deal-type badges present (with active deals) in a batch.
export const getBatchDealTypeOptions = (batch) => {
    if (!batch || !batch.results) return [];
    const set = new Set();
    batch.results.forEach(r => {
        if (r.deal_present && r.deal_badge && r.deal_badge !== 'N/A' && r.deal_badge !== 'Not applicable' && r.deal_badge !== 'Not Applicable') {
            set.add(String(r.deal_badge).trim());
        }
    });
    return Array.from(set).sort();
};
